import { MemberPicker } from "./MemberPicker";
import { Plus, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { OrganizationMemberDirectory } from "../../pages/organization-admin/types";
import { updatePermissionMembers } from "./boxTaskTypes";
import type { BoxPermissionSettings } from "./boxTaskTypes";
import styles from "./NoteWorkflows.module.css";

interface NodeTaskSettingsProps {
    value: BoxPermissionSettings;
    onChange: (value: BoxPermissionSettings) => void;
    directory: OrganizationMemberDirectory | null;
    loading: boolean;
    error: string | null;
    onRetry: () => void;
}

export function NodeTaskSettings({ value, onChange, directory, loading, error, onRetry }: NodeTaskSettingsProps) {
    const { t } = useTranslation();
    const members = directory?.members ?? [];
    const text = (key: string, defaultValue: string) => t(`settingsPage.company.noteWorkflows.tasks.${key}`, { defaultValue });
    const sections = [
        { key: "signerMemberIds", title: text("signPermission", "Sign permission") },
        { key: "viewerMemberIds", title: text("viewPermission", "View permission") }
    ] as const;
    return (
        <div className={styles.nodeTaskSettings}>
            {loading && <p role="status">{text("loading", "Loading organization members…")}</p>}
            {error && (
                <p role="alert">
                    {error}{" "}
                    <button type="button" onClick={onRetry}>
                        {text("retry", "Retry")}
                    </button>
                </p>
            )}
            {sections.map(section => {
                const selected = value[section.key];
                const update = (ids: string[]) => onChange(updatePermissionMembers(value, section.key, ids));
                return (
                    <details key={section.key} className={styles.nodeSettingsSection} open>
                        <summary>{section.title}</summary>
                        <div className={styles.nodeSettingsFields}>
                            {(selected.length ? selected : [""]).map((id, index) => (
                                <div key={index} className={styles.nodeRoleRow}>
                                    <MemberPicker
                                        label={`${section.title} ${index + 1}`}
                                        value={id}
                                        disabled={loading || !directory}
                                        options={[
                                            ...(id && !members.some(member => member.id === id)
                                                ? [{ id, label: `${text("unavailable", "Unavailable member")} · ${id}`, disabled: true }]
                                                : []),
                                            ...members.map(member => ({
                                                id: member.id,
                                                label: `${`${member.firstName} ${member.lastName}`.trim()} (${member.email})${member.id === directory?.actorMembershipId ? ` · ${text("me", "Me")}` : ""}${member.status !== "Active" ? ` · ${member.status}` : ""}`,
                                                disabled: member.status !== "Active" || (member.id !== id && selected.includes(member.id))
                                            }))
                                        ]}
                                        onChange={memberId => {
                                            const next = [...selected];
                                            next[index] = memberId;
                                            update(next.filter(Boolean));
                                        }}
                                    />
                                    {id && (
                                        <button
                                            type="button"
                                            className={styles.nodeRoleButton}
                                            aria-label={`${text("remove", "Remove")} ${section.title} ${index + 1}`}
                                            onClick={() => update(selected.filter((_, position) => position !== index))}
                                        >
                                            <X size={16} />
                                        </button>
                                    )}
                                    {index === selected.length - 1 && id && (
                                        <button
                                            type="button"
                                            className={styles.nodeRoleButton}
                                            aria-label={`${text("add", "Add")} ${section.title}`}
                                            disabled={loading || !directory}
                                            onClick={() => update([...selected, ""])}
                                        >
                                            <Plus size={16} />
                                        </button>
                                    )}
                                </div>
                            ))}
                            <small>
                                {section.key === "signerMemberIds"
                                    ? text(
                                          "signHelp",
                                          "Everyone selected must sign this box after the creator submits the card. Configure their view permission separately."
                                      )
                                    : text(
                                          "viewHelp",
                                          "Selected members can view this box. The creator always has access. View permission does not grant signing permission."
                                      )}
                            </small>
                        </div>
                    </details>
                );
            })}
        </div>
    );
}
